import type { PolicySnapshot } from '@fig/shared';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { ComparisonSection } from '@/components/ComparisonSection';
import { DismissalModal } from '@/components/DismissalModal';
import { OpportunityNotes } from '@/components/OpportunityNotes';
import { ClientCard } from '@/components/client-card';
import { ErrorState } from '@/components/common';
import { PolicyCard } from '@/components/policy-card';
import { ScoreBreakdown } from '@/components/score-breakdown';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  downloadAnnualStatement,
  useAnnualStatement,
  useDismissOpportunity,
  useOpportunity,
  useWithdrawOpportunity,
} from '@/hooks/api/useOpportunities';
import { formatDate } from '@/lib/formatters';
import { formatOpportunityStatus, getOpportunityStatusVariant } from '@/lib/opportunity-status';
import { useToast } from '@/providers/toast-provider';

import { OpportunityDetailSkeleton } from './OpportunityDetailSkeleton';

function buildPolicySnapshot(opp: {
  policy: {
    id: string;
    carrierName: string;
    productName: string | null;
    productType: string;
    accountValue: number | null;
    surrenderValue: number | null;
    premium: number | null;
    surrenderDate: string | null;
    currentCapRate: number | null;
    currentParticipationRate: number | null;
    incomeStatus: string;
    incomeBenefitValue?: number | null;
    policyAge?: number | null;
    policyStatus?: string | null;
    surrenderChargePercentage?: number | null;
    lineOfBusiness?: string | null;
    taxQualification?: string | null;
    spouseAge?: number | null;
    guaranteedRate?: number | null;
    jurisdiction?: string | null;
    owner?: { age: number | null } | null;
    relatedParties?: Array<{
      id: string | null;
      name: string | null;
      relationshipType: string;
      partyType: 'person' | 'entity' | 'unknown';
      dateOfBirth: string | null;
      age: number | null;
      isPrimary: boolean;
    }>;
    client: { age: number | null; state: string | null } | null;
  };
}): PolicySnapshot {
  const p = opp.policy;
  let yearsUntilSurrender: number | null = null;
  if (p.surrenderDate) {
    const diff = new Date(p.surrenderDate).getTime() - Date.now();
    yearsUntilSurrender = diff > 0 ? diff / (365.25 * 24 * 60 * 60 * 1000) : 0;
  }
  const relatedParties = p.relatedParties ?? [];
  const explicitJointParty = relatedParties.find((party) => {
    const relationshipType = party.relationshipType.toLowerCase();
    return relationshipType === 'spouse' || relationshipType === 'joint_owner';
  });
  const ownerAge = p.owner?.age ?? p.client?.age ?? null;
  const jurisdiction = p.jurisdiction ?? p.client?.state ?? null;

  return {
    policyId: p.id,
    carrierName: p.carrierName,
    productName: p.productName,
    productType: p.productType,
    accountValue: p.accountValue ?? 0,
    cashSurrenderValue: p.surrenderValue,
    premium: p.premium,
    surrenderDate: p.surrenderDate,
    yearsUntilSurrender,
    currentCapRate: p.currentCapRate,
    currentParticipationRate: p.currentParticipationRate,
    guaranteedRate: p.guaranteedRate ?? null,
    initialInterestRate: null,
    incomeStatus: p.incomeStatus,
    incomeBenefitValue: p.incomeBenefitValue ?? null,
    ownerAge,
    jurisdiction,
    relatedParties,
    clientAge: ownerAge,
    clientState: jurisdiction,
    spouseAge: explicitJointParty?.age ?? null,
    // Extended fields for forward payload
    lineOfBusiness: p.lineOfBusiness ?? null,
    taxQualification: p.taxQualification ?? null,
    policyAge: p.policyAge ?? null,
    policyStatus: p.policyStatus ?? null,
    surrenderChargePercentage: p.surrenderChargePercentage ?? null,
  };
}

export function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: opportunity, isLoading, error, refetch } = useOpportunity(id || '');
  const { data: annualStatement, isPending: isCheckingAnnualStatement } = useAnnualStatement(
    id || ''
  );
  const dismissOpportunity = useDismissOpportunity();
  const withdrawOpportunity = useWithdrawOpportunity();
  const { showToast } = useToast();
  const [dismissModalOpen, setDismissModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [isDownloadingStatement, setIsDownloadingStatement] = useState(false);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/opportunities');
    }
  };

  const handleDismissConfirm = async (data: { comment: string; duration?: string }) => {
    if (!id) return;
    if (
      data.duration !== '3_months' &&
      data.duration !== '6_months' &&
      data.duration !== '1_year' &&
      data.duration !== 'never'
    ) {
      return;
    }

    try {
      const result = await dismissOpportunity.mutateAsync({
        id,
        duration: data.duration,
        comment: data.comment,
      });
      showToast({
        title: 'Opportunity dismissed',
        description:
          result.dismissedUntil === null
            ? 'It will not be shown for recommendation again.'
            : `It will return after ${formatDate(result.dismissedUntil)}.`,
      });
      setDismissModalOpen(false);
    } catch (err) {
      console.error('Failed to dismiss opportunity:', err);
      showToast({
        title: 'Unable to dismiss opportunity',
        description: 'Try again or refresh the page.',
      });
    }
  };

  const handleWithdrawConfirm = async () => {
    if (!id) return;

    try {
      await withdrawOpportunity.mutateAsync({ id });
      showToast({
        title: 'Recommendation withdrawn',
        description: 'The recommendation has been marked as withdrawn from the advisor platform.',
      });
      setWithdrawModalOpen(false);
    } catch (err) {
      console.error('Failed to withdraw recommendation:', err);
      showToast({
        title: 'Unable to withdraw recommendation',
        description: 'Try again or refresh the page.',
      });
    }
  };

  if (isLoading) {
    return <OpportunityDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="py-12">
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load opportunity'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="py-12">
        <ErrorState
          title="Opportunity not found"
          message="The opportunity you're looking for doesn't exist or you don't have access."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack} aria-label="Go back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold tracking-tight">
              {opportunity.policy.carrierName}
            </h1>
            <p className="max-w-full break-words text-sm text-muted-foreground">
              {opportunity.policy.productName || opportunity.policy.productType}
              {(opportunity.policy.policyNumber ?? opportunity.policy.contractNumber) && (
                <>
                  {' '}
                  &middot; Policy #
                  {opportunity.policy.policyNumber ?? opportunity.policy.contractNumber}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5 text-right">
          <Badge
            variant={getOpportunityStatusVariant(opportunity.status)}
            className="px-3 py-1 text-sm font-semibold"
          >
            {formatOpportunityStatus(opportunity.status)}
          </Badge>
          <div className="flex items-center gap-2">
            {opportunity.status === 'new' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-muted-foreground hover:bg-slate-100 hover:text-foreground dark:hover:bg-white/10"
                onClick={() => setDismissModalOpen(true)}
                disabled={dismissOpportunity.isPending}
              >
                Dismiss
              </Button>
            )}
            {opportunity.status === 'actioned' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setWithdrawModalOpen(true)}
                disabled={withdrawOpportunity.isPending}
              >
                Withdraw
              </Button>
            )}
            <p className="text-xs text-muted-foreground">
              Scored on {formatDate(opportunity.scoredAt)}
            </p>
          </div>
        </div>
      </div>

      <div className="grid items-stretch gap-4 xl:grid-cols-[minmax(190px,0.7fr)_minmax(880px,2.8fr)]">
        <div className="min-h-0 xl:relative">
          <div className="xl:absolute xl:inset-0">
            <ClientCard policy={opportunity.policy} elevated />
          </div>
        </div>
        <PolicyCard
          policy={opportunity.policy}
          advisorName={opportunity.agentName}
          advisorStatus={opportunity.agentStatus}
          columns={4}
          isCheckingStatement={isCheckingAnnualStatement}
          onDownloadStatement={
            annualStatement?.available
              ? () => {
                  if (isDownloadingStatement) return;
                  setIsDownloadingStatement(true);
                  showToast({
                    title: 'Downloading statement',
                    description: 'Your annual statement is being downloaded...',
                  });
                  downloadAnnualStatement(id!)
                    .catch(() => {
                      showToast({
                        title: 'Download failed',
                        description: 'Unable to download the annual statement. Try again later.',
                      });
                    })
                    .finally(() => {
                      setIsDownloadingStatement(false);
                    });
                }
              : undefined
          }
        />
      </div>

      <ScoreBreakdown
        score={opportunity.propensityScore}
        factors={opportunity.scoreFactors.factors}
        estimatedValue={opportunity.estimatedValue}
      />

      <OpportunityNotes
        opportunityId={id!}
        notes={opportunity.notes}
        notesUpdatedAt={opportunity.notesUpdatedAt}
        notesUpdatedBy={opportunity.notesUpdatedBy}
      />

      {/* Product comparison */}
      <ComparisonSection
        opportunityId={id!}
        opportunityStatus={opportunity.status}
        currentPolicy={buildPolicySnapshot(opportunity)}
        onForwardComplete={handleBack}
      />

      <DismissalModal
        open={dismissModalOpen}
        onOpenChange={setDismissModalOpen}
        title="Dismiss Opportunity"
        description="This opportunity will be hidden from highly rated recommendations until the selected date."
        commentLabel="Why should this opportunity be dismissed?"
        commentPlaceholder="Add context for why this replacement opportunity should be dismissed..."
        confirmLabel="Dismiss Opportunity"
        showDurationOptions
        isSubmitting={dismissOpportunity.isPending}
        onConfirm={handleDismissConfirm}
      />

      <Dialog open={withdrawModalOpen} onOpenChange={setWithdrawModalOpen}>
        <DialogContent className="border border-border bg-surface-3 text-foreground shadow-elevation-3 sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Withdraw Recommendation</DialogTitle>
            <DialogDescription>
              This will mark the recommendation as withdrawn and remove it from the advisor platform
              workflow. The sent recommendation history will remain available for audit.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWithdrawModalOpen(false)}
              disabled={withdrawOpportunity.isPending}
            >
              Cancel
            </Button>
            <Button
              className="text-white shadow-sm transition-colors hover:opacity-90"
              style={{ backgroundColor: 'hsl(211 47% 20%)' }}
              onClick={() => void handleWithdrawConfirm()}
              disabled={withdrawOpportunity.isPending}
            >
              {withdrawOpportunity.isPending ? 'Withdrawing...' : 'Withdraw Recommendation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Actions */}
      <div className="flex justify-end gap-3 border-t pt-4">
        <Button variant="outline" onClick={handleBack}>
          Back to List
        </Button>
      </div>
    </div>
  );
}
